import React, { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  MenuItem,
  Paper,
  TextField,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { ArrowBack as BackIcon, SwapHoriz as TransferIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useVenue } from '../../../../context/VenueContext';
import { useAuth } from '../../../../context/AuthContext';
import transferService from '../../../../services/transferService';
import venueLocationService, { pickDefaultLocation } from '../../../../services/venueLocationService';
import type { VenueLocation } from '../../../../types/venue';

const todayISO = () => new Date().toISOString().split('T')[0];

const TransferForm: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { venueId } = useVenue();
  const { user } = useAuth();
  const backPath = `/admin/venues/${venueId}/transfers`;

  const [locations, setLocations] = useState<VenueLocation[]>([]);
  const [locationsLoading, setLocationsLoading] = useState(true);

  const [fromScope, setFromScope] = useState<string>('');
  const [toScope, setToScope] = useState<string>('');
  const [transferredAt, setTransferredAt] = useState(todayISO());
  const [transferredBy, setTransferredBy] = useState('');
  const [notes, setNotes] = useState('');

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (user) setTransferredBy((user as any).email ?? (user as any).firstName ?? '');
  }, [user]);

  useEffect(() => {
    if (!venueId) return;
    venueLocationService.getLocations(venueId)
      .then(locs => {
        const active = locs.filter(l => l.active);
        setLocations(active);
        // Pre-select default as the source; leave destination empty to force conscious choice
        const def = pickDefaultLocation(active);
        if (def) setFromScope(def._id);
      })
      .catch(() => {})
      .finally(() => setLocationsLoading(false));
  }, [venueId]);

  // Build location options for "to" dropdown, excluding the chosen "from"
  const toOptions = locations
    .filter(l => l._id !== fromScope)
    .map(l => ({ value: l._id, label: l.displayName ?? l.name, sub: l.type }));

  const handleFromChange = (val: string) => {
    setFromScope(val);
    // Reset to if it would now be the same as from
    if (toScope === val) setToScope('');
  };

  const isValid = fromScope !== '' && toScope !== '' && fromScope !== toScope && transferredAt;

  const handleCreate = async () => {
    setError(null);
    if (!isValid) {
      setError('Please select source and destination locations, and set a transfer date.');
      return;
    }
    setSaving(true);
    try {
      const transfer = await transferService.createTransfer(venueId, {
        fromLocationId: fromScope,
        toLocationId: toScope,
        transferredAt,
        transferredBy: transferredBy.trim() || undefined,
        notes: notes.trim() || undefined,
      });
      navigate(`/admin/venues/${venueId}/transfers/${transfer._id}`);
    } catch (err: any) {
      setError(err?.response?.data?.error?.message ?? err?.message ?? 'Failed to create transfer');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Paper sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 4px 12px rgba(0,0,0,0.08)', maxWidth: 640 }}>
        {/* Header */}
        <Box sx={{
          px: 3, py: 2.5,
          display: 'flex', alignItems: 'center', gap: 2,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
        }}>
          <Button
            startIcon={<BackIcon />}
            onClick={() => navigate(backPath)}
            sx={{ textTransform: 'none', color: theme.palette.text.secondary }}
          >
            Transfers
          </Button>
          <Divider orientation="vertical" flexItem />
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TransferIcon sx={{ color: theme.palette.primary.main, fontSize: 22 }} />
            <Typography variant="h6" fontWeight={600}>New Transfer</Typography>
          </Box>
        </Box>

        <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {error && <Alert severity="error">{error}</Alert>}

          {/* From / To */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              select
              fullWidth
              label="From Location"
              value={fromScope}
              onChange={e => handleFromChange(e.target.value)}
              disabled={locationsLoading}
              helperText="Source of stock"
              size="small"
            >
              <MenuItem value="" disabled>
                <Typography variant="body2" color="text.disabled">Select source…</Typography>
              </MenuItem>
              {locations.map(loc => (
                <MenuItem key={loc._id} value={loc._id}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>{loc.displayName}</Typography>
                    <Typography variant="caption" color="text.secondary">{loc.type}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>

            <TextField
              select
              fullWidth
              label="To Location"
              value={toScope}
              onChange={e => setToScope(e.target.value)}
              disabled={locationsLoading || !fromScope}
              helperText="Destination of stock"
              size="small"
              error={toScope === fromScope && toScope !== ''}
            >
              <MenuItem value="" disabled>
                <Typography variant="body2" color="text.disabled">Select destination…</Typography>
              </MenuItem>
              {toOptions.map(o => (
                <MenuItem key={o.value} value={o.value}>
                  <Box>
                    <Typography variant="body2" fontWeight={500}>{o.label}</Typography>
                    <Typography variant="caption" color="text.secondary">{o.sub}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </TextField>
          </Box>

          {/* Date + transferred by */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <TextField
              label="Transfer Date"
              type="date"
              value={transferredAt}
              onChange={e => setTransferredAt(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              slotProps={{ inputLabel: { shrink: true } }}
              helperText="When the physical transfer happened"
              required
            />
            <TextField
              label="Transferred By"
              value={transferredBy}
              onChange={e => setTransferredBy(e.target.value)}
              size="small"
              sx={{ flex: 1 }}
              placeholder="Name or initials"
            />
          </Box>

          {/* Notes */}
          <TextField
            label="Notes"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            fullWidth
            size="small"
            multiline
            rows={2}
            placeholder="Optional"
          />

          {/* Actions */}
          <Box sx={{
            pt: 1.5,
            borderTop: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
            display: 'flex', gap: 1.5, justifyContent: 'flex-end',
          }}>
            <Button onClick={() => navigate(backPath)} sx={{ textTransform: 'none' }}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleCreate}
              disabled={saving || !isValid}
              sx={{ textTransform: 'none', fontWeight: 600 }}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : undefined}
            >
              {saving ? 'Creating…' : 'Create Transfer'}
            </Button>
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default TransferForm;
